<template>
    <div v-if="loading" class="flex min-h-80 items-center justify-center"><Loader /></div>
    <div v-else class="p-4 ds-exchange-panel-content">
        <div class="ds-exchange-panel-title pb-5">Редактирование вакансии</div>
        <div class="ds-exchange-panel-section border-grayxlight border-b mb-2">Основные данные</div>

        <div class="mb-3">
                <div class="mb-3">
                    <div class="ds-exchange-field-label">Магазин</div>
                    <dropdown v-model="department" :options="departmentStore.myDepartments" :disabled="!canEdit">
                        {{ scheduleStore.cutStr(department.name, 23) }}
                    </dropdown>
                </div>

                <div class="flex">
                    <div>
                        <div class="mr-5 mb-2 flex-1">
                            <div class="ds-exchange-field-label">Дата</div>
                            <AppDateInput v-model="date" :disabled="!canEdit" />
                        </div>   
                    </div>

                    <div class="mr-5 mb-3">
                        <div class="ds-exchange-field-label">Время начала</div>
                        <time-picker v-model="start" :disabled="!canEdit" />
                    </div>
                    <div class="mr-5 mb-3">
                        <div class="ds-exchange-field-label">Время окончания</div>
                        <time-picker v-model="end" :disabled="!canEdit" />
                    </div>
                </div>

                <div class="mb-3 flex-1">
                    <div class="ds-exchange-field-label">Должность</div>
                    <dropdown v-model="position" :options="positions" class="text-xs" :disabled="!canEdit">
                        {{ position.name }}
                    </dropdown>
                </div>

                 <base-input v-model="price" max-length="5" class="mb-2" @input="onInputDigits" :disabled="!canEdit">Ставка в час</base-input>
        </div>

        <div class="mb-3" v-if="showExchangeType">
            <div class="ds-exchange-field-label">Тип биржи</div>
            <checkbox class="ds-exchange-checkbox" v-model="inner" :disabled="!canEdit">Внутренняя</checkbox>
            <checkbox class="ds-exchange-checkbox" v-model="outer" :disabled="!canEdit">Внешняя</checkbox>
        </div>

        <Skills :skills v-if="skills.length" />

        <div class="ds-exchange-field-label">Комментарий</div>
        <textarea v-model="comment" class="ds-exchange-textarea w-full mb-8 resize-none" maxlength="100" :disabled="!canEdit" />
        <div v-if="!canEdit" class="text-xs text-gray mb-3">
            Редактирование недоступно: по вакансии уже есть отклики или назначенный сотрудник.
        </div>
        <div class="flex justify-between items-center">
            <div @click="showModal = true">
                <icon class="text-darkred cursor-pointer opacity-40 hover:opacity-100"><IconTrash /></icon>
            </div>
            <div class="">
                <btn @click="save" :disabled="isDisabled" :class="{ 'opacity-50': isDisabled }">Сохранить</btn>
                <btn class="ml-3" type="secondary" @click="cancel">Отмена</btn>
            </div>
        </div>
    </div>

    <modal v-if="showModal" @close="closeModal()">
            <div class="w-[300px]">
                <div class="text-xl mb-3">Удалить вакансию?</div>
                <div v-if="needDeletePurpose" class="mb-3">
                    <div class="ds-exchange-field-label">Укажите причину удаления</div>
                    <textarea v-model="deletePurpose" class="ds-exchange-textarea w-full" maxlength="100" />
                </div>
                <div class="flex justify-end">
                    <btn class="px-5 mx-3" @click="removeVacancy">Да</btn>
                    <btn class="px-5" type="secondary" @click="closeModal()">Нет</btn>
                </div>
            </div>
    </modal>

</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import { Option } from '@/types'
import dayjs from 'dayjs'
import utc from "dayjs/plugin/utc"
import Loader from '@/ui/Loader.vue'
import AppDateInput from '@/ui/AppDateInput.vue'
import TimePicker from '@/components/TimePicker.vue'
import Skills from './Skills.vue'
import IconTrash from '@/ui/icons/IconTrash.vue'
import { useScheduleStore } from '@/stores/scheduleStore'
import { useDepartmentStore } from '@/stores/departmentStore'
import { useExchangeStore } from '@/stores/exchangeStore'
import { useAuthStore } from '@/stores/authStore'

const departmentStore = useDepartmentStore()
const scheduleStore = useScheduleStore()
const exchangeStore = useExchangeStore()
const authStore = useAuthStore()

const {vacancy} = defineProps(['vacancy'])

const emit = defineEmits(['close', 'save'])
dayjs.extend(utc)

const positions = computed(() => [{id: "", name: 'Выберите должность'}, ...scheduleStore.positions])
const showExchangeType = computed(() => Boolean(authStore.settings?.USE_EXCHANGE_PORTAL))

const showModal = ref<Boolean>(false)

function closeModal() {
    deletePurpose.value = ''
    showModal.value = false
}

const loading = ref(false)
const saving = ref(false)
const date = ref(dayjs(vacancy.date))
const start = ref(dayjs(vacancy.dttm_work_start_at).format('HH:mm'))
const end = ref(dayjs(vacancy.dttm_work_end_at).format('HH:mm'))
const today = new Date()
const yesterday = new Date(today)
yesterday.setDate(yesterday.getDate() - 1)
const comment = ref(vacancy.comment || '')
const position = ref<Option>(vacancy.position)
const department = ref<Option>(vacancy.department)
const price = ref(vacancy.salary_hour || '')
const skills = ref<any[]>([])
const inner = ref(vacancy.inner_exchange)
const outer = ref(vacancy.outer_exchange)
const deletePurpose = ref('')
const needDeletePurpose = ref(false)

const hasRespondedUsers = computed(() => (vacancy?.responded_users?.length ?? 0) > 0)
const hasAssignedUser = computed(() => Boolean(vacancy?.assigned_user))
const canEdit = computed(() => !hasRespondedUsers.value && !hasAssignedUser.value)

const isPriceEmpty = computed(() => price.value === '' || price.value === null || price.value === undefined)
const normalizedPrice = computed(() => Number(price.value))
const isPriceValid = computed(() => isPriceEmpty.value || (!Number.isNaN(normalizedPrice.value) && normalizedPrice.value > 0))

const isDateValid = computed(() => dayjs(yesterday).isBefore(dayjs(date.value)))
const isTimeValid = computed(() => !!start.value && !!end.value && start.value < end.value)
const isPositionValid = computed(() => Boolean(position.value?.id))

const isDisabled = computed(() => (
    !canEdit.value ||
    loading.value ||
    saving.value ||
    !isDateValid.value ||
    !isTimeValid.value ||
    !isPositionValid.value ||
    !isPriceValid.value
))

const onInputDigits = () => {
    price.value = price.value.replace(/\D/g, '');
}

const cancel = () => {
    emit('close')
}

const save = async () => {   
    if (isDisabled.value) return
    saving.value = true
    const formatDate = dayjs(date.value).format('YYYY-MM-DD')
    let data = {
        id: vacancy.id,
        date: formatDate,
        dttm_work_start_at: `${formatDate}T${start.value}:00.000Z`,
        dttm_work_end_at: `${formatDate}T${end.value}:00.000Z`,
        department: department.value.id,
        position: position.value.id,
        comment: comment.value,
        salary_hour: isPriceEmpty.value ? null : price.value,
        inner_exchange: inner.value,
        outer_exchange: outer.value
    }
    try {
        await exchangeStore.changeVacancy(data)
        emit('save')
        emit('close')
    } finally {
        saving.value = false
    }
}

const removeVacancy = async () => {
    const res = await exchangeStore.removeVacancy(vacancy.id, deletePurpose.value)
    if (res?.status === 204) {
        closeModal()
        emit('close')
    } else if (res?.status === 409) {
        needDeletePurpose.value = true
    } else {
        closeModal()
        emit('close')
    }
}

watch(position, () => {
    skills.value = scheduleStore.positions.find(item => item.id === position.value.id)?.skills || []
})
   
</script>
